/**
 * Usage docs are at the bottom of this file.
 */
import 'vidstack/player/styles/base.css';
import type { VideoPlayerElement } from '../types/video-player';
import { VidstackPlayer } from 'vidstack/global/player';
import { PlayerSrc, VideoMimeType } from 'vidstack';
import 'a11y-dialog';

/**
 * Helper function that returns a promise which resolves when the player is ready
 */
function waitUntilPlayerReady(player: any) {
  return new Promise<void>(resolve => {
    if (player.canPlay) {
      resolve();
      return;
    }

    const readyHandler = () => {
      resolve();
    };

    player.addEventListener('can-play', readyHandler, { once: true });

    setTimeout(() => {
      player.removeEventListener('can-play', readyHandler);
      resolve();
    }, 1000);
  });
}

/**
 * Safely extracts a string message from an error object
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  } else if (typeof error === 'string') {
    return error;
  } else if (error && typeof error === 'object' && 'toString' in error) {
    try {
      return JSON.stringify(error, Object.getOwnPropertyNames(error));
    } catch {
      return '[object Object]';
    }
  }
  return String(error);
}

/**
 * Helper function to safely play videos and handle "media not ready" errors
 */
async function safePlayVideo(player: any) {
  try {
    await waitUntilPlayerReady(player);

    // Check if the player is already playing
    if (player.paused === false) {
      return; // Player is already playing, no need to call play()
    }

    // Special handling for YouTube iframe API
    if (typeof player.src === 'string' && player.src.includes('youtube.com')) {
      const playerState = player.getPlayerState?.();
      if (playerState === 1) {
        // 1 indicates 'playing' state in YouTube API
        return;
      }
    }

    try {
      return await player.play();
    } catch (err) {
      const errorMessage = getErrorMessage(err);

      if (!errorMessage.includes('play request failed') && !errorMessage.includes('auto-play request failed')) {
        console.error('Video Player | Error', err);
        throw err;
      }

      return new Promise(resolve => {
        setTimeout(async () => {
          try {
            const result = await player.play();
            resolve(result);
          } catch (secondErr) {
            const secondErrorMessage = getErrorMessage(secondErr);
            if (
              !secondErrorMessage.includes('play request failed') &&
              !secondErrorMessage.includes('auto-play request failed')
            ) {
              console.error('Video Player | Error on retry', secondErr);
            } else {
              resolve(undefined);
            }
          }
        }, 500);
      });
    }
  } catch (err) {
    const errorMessage = getErrorMessage(err);
    if (!errorMessage.includes('play request failed') && !errorMessage.includes('auto-play request failed')) {
      console.error('Video Player | Error during preparation', err);
    }
    throw err;
  }
}

async function initVideoPlayer(playerElement: VideoPlayerElement) {
  if (!playerElement.dataset.videoSrc || playerElement.dataset.videoSrc === '') {
    console.error('Video Player | URL missing from data-video-src attribute.');
    return;
  }
  let url = playerElement.dataset.videoSrc;

  const backgroundVideo = getAttribute(playerElement.dataset.videoBackground) ?? false;
  const modal = getAttribute(playerElement.dataset.videoModal) ?? false;
  const poster = getAttribute(playerElement.dataset.videoPoster) ?? '';
  let controls = getAttribute(playerElement.dataset.videoControls) ?? true;
  let muted = getAttribute(playerElement.dataset.videoMuted) ?? false;
  let autoplay = getAttribute(playerElement.dataset.videoAutoplay) ?? false;
  let loop = getAttribute(playerElement.dataset.videoLoop) ?? false;

  const title = getAttribute(playerElement.dataset.videoTitle) ?? null;

  if (backgroundVideo) {
    controls = false;
    muted = true;
    autoplay = true;
    loop = true;
  }

  if (modal) {
    controls = true;
    muted = false;
    autoplay = false;
    loop = false;
  }

  // Handle YouTube and Vimeo URLs
  // Update the YouTube URL handling logic to ensure special characters in video IDs are handled correctly
  const isYouTube = url.includes('youtube.com') || url.includes('youtu.be');
  if (isYouTube) {
    let videoId = '';
    try {
      if (url.includes('youtube.com')) {
        const parsedUrl = new URL(url);
        videoId = parsedUrl.searchParams.get('v') ?? '';
        // Handle /embed/ links
        if (!videoId && parsedUrl.pathname.startsWith('/embed/')) {
          videoId = parsedUrl.pathname.split('/embed/')[1];
        }
      } else if (url.includes('youtu.be')) {
        // youtu.be/VIDEO_ID
        const parsedUrl = new URL(url);
        videoId = parsedUrl.pathname.replace('/', '');
      }
    } catch (e) {
      console.error('Video Player | Invalid YouTube URL, unable to extract video ID.', e);
      return;
    }
    if (!videoId) {
      console.error('Video Player | Invalid YouTube URL, unable to extract video ID.');
      return;
    }
    url = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${autoplay ? 'autoplay=1&' : ''}${loop ? 'loop=1&playlist=' : ''}${muted ? 'mute=1&' : ''}${!controls ? 'controls=0&modestbranding=1&' : ''}`;
  } else if (url.includes('vimeo.com')) {
    // Support tokenised Vimeo links
    try {
      const parsedUrl = new URL(url);
      const pathParts = parsedUrl.pathname.replace(/^\//, '').split('/');
      const videoId = pathParts[0];
      const hash = pathParts[1] || '';
      let vimeoQuery = '';
      if (hash) {
        vimeoQuery += `h=${encodeURIComponent(hash)}&`;
      }
      vimeoQuery += `${autoplay ? 'autoplay=1&autopause=0&' : ''}${loop ? 'loop=1&' : ''}${muted ? 'muted=1&' : ''}${!controls ? 'background=1&' : ''}`;
      vimeoQuery = vimeoQuery.replace(/&$/, '');
      url = `https://player.vimeo.com/video/${videoId}${vimeoQuery ? '?' + vimeoQuery : ''}`;
    } catch (e) {
      console.error('Video Player | Invalid Vimeo URL, unable to extract video ID/hash.', e);
      return;
    }
  }

  /**
   * VidStack cannot infer the type of a video when it comes from
   * the Opti DAM and will fail to load. Need to check manually
   * and add the type. If one is missing, fallback to video/mp4.
   */
  let source: PlayerSrc | null = null;
  if (url.includes('optimizely') || url.includes('globalassets')) {
    const type = playerElement.dataset.videoType;
    source = {
      src: url,
      type: (type as VideoMimeType) ?? 'video/mp4',
    };
    muted = true;
  }

  const player = await VidstackPlayer.create({
    target: playerElement,
    src: source ?? url,
    playsInline: true,
    viewType: 'video',
    streamType: 'on-demand',
    loop: loop as boolean,
    controls: controls as boolean,
    muted: muted as boolean,
    poster: poster as string,
    autoPlay: autoplay as boolean,
    title: title !== '' ? (title as string) : undefined,
    preload: 'auto',
  });

  // Set player to be accessible on the element itself.
  playerElement.setAttribute('player', '');
  playerElement.player = player as unknown as HTMLVideoElement;

  // Pass through error message from playerElement to player web component
  if (playerElement.dataset.videoErrorMessage && playerElement.dataset.videoErrorMessage !== '') {
    player.dataset.videoErrorMessage = playerElement.dataset.videoErrorMessage;
  } else {
    player.dataset.videoErrorMessage = 'There was an issue attempting to play this video, please try again.';
  }

  /**
   * data-error attribute will be automatically added
   * to element by Vidstack. Error is rendered based on
   * this.
   *
   * Filter out common autoplay policy errors to keep the console clean
   */
  player.addEventListener('error', err => {
    const errorMessage = getErrorMessage(err);

    if (!errorMessage.includes('play request failed') && !errorMessage.includes('auto-play request failed')) {
      console.log('Video Player | Error', err);
    }
  });

  /**
   * If this video player is being used inside a modal,
   * listen for open and close custom events to control
   * playback.
   */
  if (modal) {
    if (!playerElement.dataset.modalEventsInitialized) {
      playerElement.dataset.modalEventsInitialized = 'true';

      const modalContainer = playerElement.closest('[data-a11y-dialog]');

      if (modalContainer) {
        modalContainer.addEventListener('show', async () => {
          document.body.classList.add('lightbox-scroll-lock');

          setTimeout(() => {
            // Set muted state according to the attribute, not forced
            if (typeof muted === 'boolean') {
              playerElement.player.muted = muted;
            }

            // Wait for can-play event before calling play
            const onCanPlay = async () => {
              playerElement.player.removeEventListener('can-play', onCanPlay);
              try {
                await safePlayVideo(playerElement.player);
              } catch (error) {
                console.error('Video Player | Error', error);
              }
            };
            playerElement.player.addEventListener('can-play', onCanPlay, { once: true });
          }, 400); // Slightly increased delay for modal animation
        });

        modalContainer.addEventListener('hide', () => {
          document.body.classList.remove('lightbox-scroll-lock');

          if (playerElement.player) {
            playerElement.player.pause();
          }
        });
      }
    }
  }

  /**
   * Improved autoplay handling that better manages the "media not ready" error.
   * Uses a more reliable approach to ensure the video plays when it's ready.
   */
  let hasSetMuted = false;
  player.addEventListener('can-play', () => {
    setTimeout(async () => {
      try {
        // Only set muted state once after initial load
        if (!hasSetMuted && typeof muted === 'boolean') {
          player.muted = muted;
          hasSetMuted = true;
        }

        // Only auto-play if autoplay is true and video is paused
        if (autoplay && player.paused) {
          await safePlayVideo(player);
        }

        // Remove poster image after video starts playing
        const poster = playerElement.parentElement?.querySelector<HTMLImageElement>('[data-video-poster-img]');
        if (poster) {
          poster.style.display = 'none';
        }

        // Ensure alt text is set on any poster image inside media-poster (Vidstack)
        const vidstackPosterImg = playerElement.parentElement?.querySelector('media-poster img');
        if (vidstackPosterImg && !vidstackPosterImg.hasAttribute('alt')) {
          vidstackPosterImg.setAttribute('alt', 'Video poster image');
        }
      } catch (error) {
        console.error('Video Player | Autoplay failed', error);
      }
    }, 100);
  });
}

function getAttribute(attribute: string | undefined): string | boolean | null {
  if (attribute === '') {
    return true;
  } else if (attribute === undefined) {
    return null;
  } else if (attribute === 'true') {
    return true;
  } else if (attribute === 'false') {
    return false;
  } else {
    return attribute;
  }
}

/**
 * Generic player control functions that can be exported and used externally.
 * These handle the "media not ready" error properly.
 */
export function playVideoPlayer(element: VideoPlayerElement) {
  if (element.player) {
    return safePlayVideo(element.player);
  }
  return Promise.reject(new Error('Video player not initialized'));
}

export function pauseVideoPlayer(element: VideoPlayerElement) {
  if (element.player) {
    element.player.pause();
    return Promise.resolve();
  }
  return Promise.reject(new Error('Video player not initialized'));
}

const videoElements = document.querySelectorAll('[data-video-player]');
videoElements.forEach(initVideoPlayer);
